import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, Eyebrow } from "./Reveal";

const VALLEY_IMG =
  "https://images.unsplash.com/photo-1543213324-024b81adc95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxuZXclMjB6ZWFsYW5kJTIwcm9sbGluZyUyMGdyZWVuJTIwaGlsbHMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkzNzQzMDYyfDA&ixlib=rb-4.1.0&q=85";

export const Kaupapa = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="kaupapa" className="relative bg-[#FBF9F4] py-28 sm:py-40" data-testid="kaupapa-section">
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <FadeUp>
                <Eyebrow>01 — Our Kaupapa</Eyebrow>
              </FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="font-serif-brand text-4xl sm:text-5xl lg:text-6xl text-[#121512] leading-[1.05] tracking-tight mt-6">
                  Rooted in whenua.
                  <br />
                  <span className="italic text-[#1E3A2B]">Guided by tikanga.</span>
                </h2>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="mt-8 text-base sm:text-lg text-[#5A665D] leading-relaxed max-w-md">
                  Aotea Biotech is a kaupapa Māori medicinal cannabis cultivation and research
                  company, planned for Māori freehold land at Aotea, Kāwhia — where volcanic
                  soils and coastal air shape something no overseas competitor can replicate.
                </p>
              </FadeUp>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-14">
            <FadeUp delay={0.15}>
              <div ref={imgRef} className="relative overflow-hidden rounded-2xl aspect-[16/10]" data-testid="kaupapa-image-frame">
                <motion.img
                  src={VALLEY_IMG}
                  alt="Rolling green hills of the Kāwhia region, Aotearoa New Zealand"
                  style={{ y }}
                  className="absolute inset-0 w-full h-[118%] object-cover will-change-transform"
                />
                <div className="absolute bottom-4 left-4 rounded-full bg-[#121512]/70 backdrop-blur-md px-4 py-2">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[#FBF9F4]">Aotea / Kāwhia — West Coast, North Island</p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <blockquote className="border-l-2 border-[#D4A359] pl-7 sm:pl-9" data-testid="kaupapa-quote">
                <p className="font-serif-brand italic text-2xl sm:text-3xl text-[#1E3A2B] leading-snug">
                  “An established, licensed kaupapa Māori cultivation and research business —
                  producing pharmaceutical-grade product and unique cultivars for Aotearoa,
                  while providing employment for local rangatahi.”
                </p>
                <cite className="not-italic font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[#5A665D] block mt-5">
                  Company Mission — Aotea Biotech Ltd
                </cite>
              </blockquote>
            </FadeUp>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {[
                {
                  title: "Kaitiakitanga",
                  body: "Stewardship of land and water sits at the centre of every decision — from site design to harvest.",
                },
                {
                  title: "Community first",
                  body: "Career pathways in bioscience and biotechnology for rangatahi and whānau of the Kāwhia rohe.",
                },
                {
                  title: "Research-driven",
                  body: "Site trials and documented genetics back every claim we make about our provenance.",
                },
                {
                  title: "Tikanga-led governance",
                  body: "Advised on tikanga, whenua, security and cultivation — cultural practice embedded in operations.",
                },
              ].map((v, i) => (
                <FadeUp key={v.title} delay={i * 0.08}>
                  <div className="border-t border-[#1E3A2B]/15 pt-5" data-testid={`kaupapa-value-${i}`}>
                    <h3 className="font-serif-brand text-xl sm:text-2xl text-[#121512]">{v.title}</h3>
                    <p className="text-sm text-[#5A665D] leading-relaxed mt-2.5">{v.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
